import { Table, TableColumnsType, Button, Space, Modal, message, Form, Input, InputNumber, Card, Drawer } from 'antd';
import React, { useState } from 'react';
import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import FilterArea, { FilterOptions, DEFAULT_FILTER_OPTIONS, convertToGraphQLFilters } from './components/FilterArea';
import StandardizedAddressDetailDrawer from './components/StandardizedAddressDetailDrawer';
import AddStandardizedAddressDrawer from './components/AddStandardizedAddressDrawer';
import AddAddressFromApiDrawer from './components/AddAddressFromApiDrawer';
import AddressAliasListModal from './components/AddressAliasListModal';
import AddressConfigModal from './components/AddressConfigModal';
import BatchDeleteAliasModal from './components/BatchDeleteAliasModal';
import WarmUpAliasCacheModal from './components/WarmUpAliasCacheModal';
import OrderStandardizedAddressModal from './components/OrderStandardizedAddressModal';
import {
  GET_STANDARDIZED_ADDRESS_LISTING,
  GetStandardizedAddressListingResponse,
  StandardizedAddressType,
    ANALYZE_ADDRESS_SIMPLE
} from './graphql/queries/getStandardizedAddressListing';
import { HasResource } from '../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';
import {AnalyzeAddressSimpleResponse} from "../address-whitelist/graphql/addressWhitelistGraphql";
import {
  GET_STANDARDIZED_ADDRESS_DETAIL,
  GetStandardizedAddressDetailResponse,
} from './graphql/queries/getStandardizedAddressDetail';
import {
  UPDATE_STANDARDIZED_ADDRESS,
  UpdateStandardizedAddressResponse,
} from './graphql/mutations/updateStandardizedAddress';
import {
  QUERY_ADDRESS_METADATA,
  UPDATE_ADDRESS_METADATA,
  AddressMetadata,
  QueryAddressMetadataResponse,
  QueryAddressMetadataVariables,
  UpdateAddressMetadataResponse,
  UpdateAddressMetadataVariables,
} from '../../central/test/graphql/addressMetadata';
import AccessCodeImportPage from '../../central/test/AccessCodeImportPage';
import AddressBlacklistTestPage from '../../central/test/AddressBlacklistTestPage';

const { TextArea } = Input;

const StandardizedAddressPage: React.FC = () => {
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [addDrawerVisible, setAddDrawerVisible] = useState(false);
  const [metadataModalVisible, setMetadataModalVisible] = useState(false);
  const [currentMetadata, setCurrentMetadata] = useState<AddressMetadata | null>(null);
  const [metadataForm] = Form.useForm<AddressMetadata>();
  const [accessCodeImportVisible, setAccessCodeImportVisible] = useState(false);
  const [blacklistImportVisible, setBlacklistImportVisible] = useState(false);
  const [aliasModalVisible, setAliasModalVisible] = useState(false);
  const [aliasAddressId, setAliasAddressId] = useState<string | null>(null);
  const [aliasAddressText, setAliasAddressText] = useState<string>('');
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [batchDeleteAliasVisible, setBatchDeleteAliasVisible] = useState(false);
  const [warmUpAliasCacheVisible, setWarmUpAliasCacheVisible] = useState(false);
  const [orderStandardizedAddressVisible, setOrderStandardizedAddressVisible] = useState(false);
  const [addFromApiVisible, setAddFromApiVisible] = useState(false);
  const [lnglatModalVisible, setLnglatModalVisible] = useState(false);
  const [lnglatRecord, setLnglatRecord] = useState<StandardizedAddressType | null>(null);
  const [lnglatLatitude, setLnglatLatitude] = useState<number | null>(null);
  const [lnglatLongitude, setLnglatLongitude] = useState<number | null>(null);

  const graphQLFilters = convertToGraphQLFilters(filterOptions);

  const { data, loading, refetch } = useQuery<GetStandardizedAddressListingResponse>(
    GET_STANDARDIZED_ADDRESS_LISTING,
    {
      variables: {
        filters: graphQLFilters,
        pageRequest: {
          page: currentPage,
          size: pageSize,
          sortBy: 'updatedAt',
        },
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const handleFilter = (options: FilterOptions) => {
    setFilterOptions(options);
    setCurrentPage(1);
    refetch({
      filters: convertToGraphQLFilters(options),
      pageRequest: {
        page: 1,
        size: pageSize,
        sortBy: 'updatedAt',
      },
    });
  };
  const [fetchDetail] = useLazyQuery<GetStandardizedAddressDetailResponse>(
    GET_STANDARDIZED_ADDRESS_DETAIL,
    { fetchPolicy: 'network-only' }
  );

  const [analyzeAddress] = useLazyQuery<AnalyzeAddressSimpleResponse>(
    ANALYZE_ADDRESS_SIMPLE,
    { fetchPolicy: 'network-only' }
  );

  const [queryAddressMetadata, { loading: metadataQuerying }] = useMutation<
    QueryAddressMetadataResponse,
    QueryAddressMetadataVariables
  >(QUERY_ADDRESS_METADATA);

  const [updateAddressMetadata, { loading: metadataSaving }] = useMutation<
    UpdateAddressMetadataResponse,
    UpdateAddressMetadataVariables
  >(UPDATE_ADDRESS_METADATA);
  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
    refetch({
      filters: graphQLFilters,
      pageRequest: {
        page,
        size,
        sortBy: 'updatedAt',
      },
    });
  };

  const [updateStandardizedAddress, { loading: lnglatUpdating }] = useMutation<
    UpdateStandardizedAddressResponse
  >(UPDATE_STANDARDIZED_ADDRESS);

  const handleOpenLnglatModal = (record: StandardizedAddressType) => {
    setLnglatRecord(record);
    // 预填当前坐标，便于微调
    setLnglatLatitude(record.latitude != null ? Number(record.latitude) : null);
    setLnglatLongitude(record.longitude != null ? Number(record.longitude) : null);
    setLnglatModalVisible(true);
  };

  const handleLnglatSubmit = async () => {
    if (!lnglatRecord) return;
    if (lnglatLatitude === null || lnglatLatitude < -90 || lnglatLatitude > 90) {
      message.error('请输入合法纬度（-90 ~ 90）');
      return;
    }
    if (lnglatLongitude === null || lnglatLongitude < -180 || lnglatLongitude > 180) {
      message.error('请输入合法经度（-180 ~ 180）');
      return;
    }
    try {
      // 复用更新接口：仅传坐标，后端以最高优先级来源（DRIVER）写入
      const { data } = await updateStandardizedAddress({
        variables: {
          input: {
            id: lnglatRecord.id,
            latitude: lnglatLatitude,
            longitude: lnglatLongitude,
          },
        },
      });
      const result = data?.updateStandardizedAddress;
      if (result?.success) {
        message.success('经纬度更新成功');
        setLnglatModalVisible(false);
        void refetch();
      } else {
        message.error(result?.errorMessage || '经纬度更新失败');
      }
    } catch (err) {
      message.error(`经纬度更新失败：${(err as Error).message}`);
    }
  };

  const handleUpdateApiData = (record: StandardizedAddressType) => {
    Modal.confirm({
      title: '确认更新',
      content: '当前操作会重新调用外部api进行标准地址数据更新，是否确认更新？',
      onOk: async () => {
        const { data: detailData, error: detailError } = await fetchDetail({
          variables: { id: record.id },
        });
        if (detailError || !detailData?.getStandardizedAddressDetail) {
          message.error('获取地址详情失败');
          return;
        }
        const detail = detailData.getStandardizedAddressDetail;

        const { data, error } = await analyzeAddress({
          variables: {
            streetAddress: detail.unifiedStreetAddress,
            city: detail.cityName ?? '',
            state: detail.stateAbbreviation,
            zipCode: detail.zipcode,
            forceExternalApiMatch: true,
          },
        });
        if (error) {
          message.error(`更新失败：${error.message}`);
          return;
        }
        const result = data?.analyzeAddressSimple;
        if (result?.entityComponents) {
          message.success('更新成功');
          void refetch();
        } else {
          message.error(`更新失败：${result?.validationReason ?? '未匹配到地址'}`);
        }
      },
    });
  };

  const handleViewData = async (record: StandardizedAddressType) => {
    setMetadataModalVisible(true);
    setCurrentMetadata(null);
    metadataForm.resetFields();
    try {
      const { data } = await queryAddressMetadata({
        variables: { addressId: Number(record.id) },
      });
      if (!data?.queryAddressMetadata) {
        message.info('未查询到对应的地址元数据信息');
        return;
      }
      const metadata = data.queryAddressMetadata;
      setCurrentMetadata(metadata);
      metadataForm.setFieldsValue({ ...metadata });
    } catch (error: any) {
      message.error(error?.message ?? '查询失败，请稍后重试');
    }
  };

  const handleSaveMetadata = async () => {
    try {
      const values = await metadataForm.validateFields();
      if (!currentMetadata) {
        message.warning('请先加载地址元数据信息');
        return;
      }
      Modal.confirm({
        title: '确认修改',
        content: '请谨慎修改地址元数据信息，会影响下单地址拦截校验，是否确认修改',
        onOk: async () => {
          const { __typename: _t1, ...cleanCurrent } = (currentMetadata as any) ?? {};
          const { __typename: _t2, ...cleanValues } = (values as any) ?? {};
          const payload: AddressMetadata = {
            ...(cleanCurrent as AddressMetadata),
            ...(cleanValues as AddressMetadata),
            addressId: currentMetadata.addressId,
          };
          const { data } = await updateAddressMetadata({ variables: { input: payload } });
          if (data?.updateAddressMetadata) {
            message.success('保存成功');
            setMetadataModalVisible(false);
            setCurrentMetadata(null);
            metadataForm.resetFields();
          } else {
            message.error('保存失败');
          }
        },
      });
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.message ?? '保存失败，请稍后重试');
    }
  };

  const columns: TableColumnsType<StandardizedAddressType> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      align: 'center',
    },
    {
      title: '标准地址',
      dataIndex: 'fullStandardAddress',
      key: 'fullStandardAddress',
      ellipsis: true,
      width: 400,
    },
    {
      title: '坐标',
      key: 'coordinates',
      width: 200,
      align: 'center',
      render: (_, record) => (
        <span>
          {record.latitude && record.longitude
            ? `${record.latitude}, ${record.longitude}`
            : '-'}
        </span>
      ),
    },
    {
      title: '单元类型',
      dataIndex: 'unitType',
      key: 'unitType',
      width: 120,
      align: 'center',
      render: (text: string | null) => text || '-',
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      align: 'center',
      render: (text: string) => {
        if (!text) return '-';
        return new Date(text).toLocaleString();
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 260,
      fixed: 'right' as const,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            size="small"
            onClick={() => {
              setSelectedAddressId(record.id);
              setDrawerVisible(true);
            }}
          >
            查看
          </Button>
          <HasResource resourceCodes={[ResourceCode.standardizedAddress.UPDATE]}>
            <Button
              type="link"
              size="small"
              onClick={() => handleOpenLnglatModal(record)}
            >
              更新经纬度
            </Button>
          </HasResource>
          <Button
            type="link"
            size="small"
            onClick={() => handleUpdateApiData(record)}
          >
            更新API数据
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleViewData(record)}
          >
            查看元数据
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setAliasAddressId(record.id);
              setAliasAddressText(record.fullStandardAddress);
              setAliasModalVisible(true);
            }}
          >
            查看别名清单
          </Button>
        </Space>
      ),
    },
  ];

  const pageData = data?.getStandardizedAddressListing;

  return (
    <div className="p-6">
      <h4 className="text-xl font-bold mb-4">标准化地址平台</h4>

      <FilterArea filters={filterOptions} handleFilter={handleFilter} />

      <div className="mb-4 mt-4">
      <HasResource resourceCodes={[ResourceCode.standardizedAddress.BATCH_SAVE]}>
        <Button
          type="primary"
          onClick={() => setAddDrawerVisible(true)}
        >
          新增标准化地址
        </Button>
      </HasResource>
      <Button
        style={{ marginLeft: 8 }}
        onClick={() => setAddFromApiVisible(true)}
      >
        添加标准化地址
      </Button>
      <Button
        style={{ marginLeft: 8 }}
        onClick={() => setAccessCodeImportVisible(true)}
      >
        导入门禁code
      </Button>
      <Button
        style={{ marginLeft: 8 }}
        onClick={() => setBlacklistImportVisible(true)}
      >
        导入黑名单
      </Button>
      <Button
        style={{ marginLeft: 8 }}
        onClick={() => setConfigModalVisible(true)}
      >
        灰度配置
      </Button>
      <Button
        style={{ marginLeft: 8 }}
        onClick={() => setBatchDeleteAliasVisible(true)}
      >
        批量删除别名
      </Button>
      {/* 与后端 warmUpAliasMatchCache 的 @RequireResource 对齐，无权限时不展示，避免点开弹窗才报错 */}
      <HasResource resourceCodes={[ResourceCode.standardizedAddress.UPDATE]}>
        <Button
          style={{ marginLeft: 8 }}
          onClick={() => setWarmUpAliasCacheVisible(true)}
        >
          别名缓存初始化
        </Button>
      </HasResource>
      {/* 只读排查工具，后端接口资源码与本页面一致（STANDARDIZED_ADDRESS），不再额外门控 */}
      <Button
        style={{ marginLeft: 8 }}
        onClick={() => setOrderStandardizedAddressVisible(true)}
      >
        订单标准化地址查询
      </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <Table
          columns={columns}
          dataSource={pageData?.content || []}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: pageData?.totalElements || 0,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
        />
      </div>

      <StandardizedAddressDetailDrawer
        visible={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedAddressId(null);
          refetch();
        }}
        addressId={selectedAddressId}
      />

      <AddStandardizedAddressDrawer
        visible={addDrawerVisible}
        onClose={() => {
          setAddDrawerVisible(false);
          refetch();
        }}
        onSuccess={() => {
          refetch();
        }}
      />

      <AddAddressFromApiDrawer
        visible={addFromApiVisible}
        onClose={() => setAddFromApiVisible(false)}
        onSuccess={() => refetch()}
      />

      <Modal
        title="更新经纬度"
        open={lnglatModalVisible}
        onCancel={() => setLnglatModalVisible(false)}
        onOk={handleLnglatSubmit}
        confirmLoading={lnglatUpdating}
        destroyOnHidden
      >
        <div style={{ marginBottom: 16, wordBreak: 'break-all' }}>
          地址：{lnglatRecord?.fullStandardAddress}
        </div>
        <div style={{ marginBottom: 8 }}>纬度：</div>
        <InputNumber
          style={{ width: '100%', marginBottom: 16 }}
          placeholder="如 40.6301234（-90 ~ 90）"
          min={-90}
          max={90}
          step={0.000001}
          value={lnglatLatitude}
          onChange={setLnglatLatitude}
        />
        <div style={{ marginBottom: 8 }}>经度：</div>
        <InputNumber
          style={{ width: '100%' }}
          placeholder="如 -74.0201234（-180 ~ 180）"
          min={-180}
          max={180}
          step={0.000001}
          value={lnglatLongitude}
          onChange={setLnglatLongitude}
        />
      </Modal>

      <Drawer
        title="元数据详情"
        open={metadataModalVisible}
        onClose={() => {
          setMetadataModalVisible(false);
          setCurrentMetadata(null);
          metadataForm.resetFields();
        }}
        width={900}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setMetadataModalVisible(false);
                  setCurrentMetadata(null);
                  metadataForm.resetFields();
                }}
              >
                取消
              </Button>
              <Button
                type="primary"
                loading={metadataSaving}
                onClick={handleSaveMetadata}
              >
                确认
              </Button>
            </Space>
          </div>
        }
      >
        <Card loading={metadataQuerying} size="small">
          <Form<AddressMetadata> form={metadataForm} layout="vertical">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Form.Item label="ID" name="id">
                <InputNumber style={{ width: '100%' }} disabled />
              </Form.Item>
              <Form.Item label="Address ID" name="addressId">
                <InputNumber style={{ width: '100%' }} disabled />
              </Form.Item>
              <Form.Item label="DPV Match Code" name="dpvMatchCode">
                <Input />
              </Form.Item>
              <Form.Item label="DPV Footnotes" name="dpvFootnotes">
                <Input />
              </Form.Item>
              <Form.Item label="DPV Vacant" name="dpvVacant">
                <Input />
              </Form.Item>
              <Form.Item label="DPV CMRA" name="dpvCmra">
                <Input />
              </Form.Item>
              <Form.Item label="RDI" name="rdi">
                <Input />
              </Form.Item>
              <Form.Item label="ZIP Type" name="zipType">
                <Input />
              </Form.Item>
              <Form.Item label="Record Type" name="recordType">
                <Input />
              </Form.Item>
              <Form.Item label="County Name" name="countyName">
                <Input />
              </Form.Item>
              <Form.Item label="County FIPS" name="countyFips">
                <Input />
              </Form.Item>
              <Form.Item label="Precision" name="precision">
                <Input />
              </Form.Item>
              <Form.Item label="Precision Score" name="precisionScore">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="Address Type Primary" name="addressTypePrimary">
                <Input />
              </Form.Item>
              <Form.Item label="Classification Source" name="classificationSource">
                <Input />
              </Form.Item>
              <Form.Item label="Classification Reason" name="classificationReason">
                <Input />
              </Form.Item>
              <Form.Item label="Source" name="source">
                <Input />
              </Form.Item>
              <Form.Item label="Verification Method" name="verificationMethod">
                <Input />
              </Form.Item>
            </div>
            <Form.Item label="Source Metadata JSON" name="sourceMetadataJson">
              <TextArea rows={6} />
            </Form.Item>
          </Form>
        </Card>
      </Drawer>

      <AddressAliasListModal
        visible={aliasModalVisible}
        addressId={aliasAddressId}
        addressText={aliasAddressText}
        onClose={() => {
          setAliasModalVisible(false);
          setAliasAddressId(null);
          setAliasAddressText('');
        }}
      />

      <AddressConfigModal
        visible={configModalVisible}
        onClose={() => setConfigModalVisible(false)}
      />

      <BatchDeleteAliasModal
        visible={batchDeleteAliasVisible}
        onClose={() => setBatchDeleteAliasVisible(false)}
      />

      <WarmUpAliasCacheModal
        visible={warmUpAliasCacheVisible}
        onClose={() => setWarmUpAliasCacheVisible(false)}
      />

      <OrderStandardizedAddressModal
        visible={orderStandardizedAddressVisible}
        onClose={() => setOrderStandardizedAddressVisible(false)}
      />

      <Drawer
        title="导入门禁code"
        open={accessCodeImportVisible}
        onClose={() => setAccessCodeImportVisible(false)}
        width={800}
        footer={null}
        destroyOnHidden
      >
        <AccessCodeImportPage />
      </Drawer>

      <Drawer
        title="导入黑名单"
        open={blacklistImportVisible}
        onClose={() => setBlacklistImportVisible(false)}
        width={800}
        footer={null}
        destroyOnHidden
      >
        <AddressBlacklistTestPage />
      </Drawer>
    </div>
  );
};

export default StandardizedAddressPage;

